/**
 * ============================================================================
 * CapaWorkbenchFilters — status / criticality / source / owner filters.
 * Owner: Phase 7 — CAPA Module agent.
 * URS rows: CAPA-001..020.
 * ============================================================================ */

import { useTranslation } from 'react-i18next';
import styles from './CapaWorkbench.module.css';
import type {
  CapaQueryParams,
  CapaStatus,
  CapaCriticality,
  CapaSourceKind,
} from './useCapasQuery';

export interface CapaWorkbenchFiltersProps {
  value: CapaQueryParams;
  onChange: (next: CapaQueryParams) => void;
}

const STATUSES: CapaStatus[] = [
  'IDENTIFIED',
  'INVESTIGATION',
  'ACTION_PLAN',
  'IMPLEMENTATION',
  'EFFECTIVENESS_CHECK',
  'CLOSED',
  'CANCELLED',
];

const CRITICALITIES: CapaCriticality[] = ['CRITICAL', 'MAJOR', 'MINOR'];

const SOURCES: CapaSourceKind[] = [
  'DEVIATION',
  'OOS',
  'AUDIT_FINDING',
  'RISK',
  'COMPLAINT',
  'MGMT_REVIEW',
  'CC_OUTCOME',
  'OTHER',
];

export function CapaWorkbenchFilters({
  value,
  onChange,
}: CapaWorkbenchFiltersProps) {
  const { t } = useTranslation();

  function update<K extends keyof CapaQueryParams>(
    key: K,
    raw: string | undefined,
  ) {
    const next = { ...value };
    if (!raw) delete next[key];
    else (next as Record<string, unknown>)[key] = raw;
    onChange(next);
  }

  function reset() {
    onChange({});
  }

  return (
    <div className={styles.filterBar} role="group" aria-label="CAPA filters">
      <label className={styles.filterField}>
        <span className={styles.filterLabel}>
          {t('capa.filters.status', { defaultValue: 'Status' })}
        </span>
        <select
          className={styles.filterSelect}
          value={value.status ?? ''}
          onChange={(e) => update('status', e.target.value || undefined)}
        >
          <option value="">
            {t('capa.filters.allStatuses', { defaultValue: 'All statuses' })}
          </option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </label>

      <label className={styles.filterField}>
        <span className={styles.filterLabel}>
          {t('capa.filters.criticality', { defaultValue: 'Criticality' })}
        </span>
        <select
          className={styles.filterSelect}
          value={value.criticality ?? ''}
          onChange={(e) => update('criticality', e.target.value || undefined)}
        >
          <option value="">
            {t('capa.filters.allCriticalities', {
              defaultValue: 'All criticalities',
            })}
          </option>
          {CRITICALITIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </label>

      <label className={styles.filterField}>
        <span className={styles.filterLabel}>
          {t('capa.filters.source', { defaultValue: 'Source' })}
        </span>
        <select
          className={styles.filterSelect}
          value={value.sourceKind ?? ''}
          onChange={(e) => update('sourceKind', e.target.value || undefined)}
        >
          <option value="">
            {t('capa.filters.allSources', { defaultValue: 'All sources' })}
          </option>
          {SOURCES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </label>

      <button
        type="button"
        className={styles.filterClear}
        onClick={reset}
        aria-label={t('capa.filters.resetAria', {
          defaultValue: 'Clear all filters',
        })}
      >
        {t('common.reset', { defaultValue: 'Reset' })}
      </button>
    </div>
  );
}
